// Client for the control API (docs/controlapi.yaml). Types mirror the
// OpenAPI schemas; keep them in step when the contract changes.

import type { NodeClock, NodeImpact, NodeLoad, NodeRelay, RelayRole } from './admin/network/api'

export type Role = 'user' | 'admin'

export interface User {
  id: number
  email: string
  role: Role
  status: 'active' | 'blocked'
  blocked_reason?: string
  created_at: string
  /** first-run guide: pending = show it instead of the Live screen */
  setup: SetupState
  /** console features an admin turned on (PATCH /api/admin/users/{id}/features); absent = none */
  features?: UserFeatures
  /** separate user and admin names: which one answered; absent = one name for everything */
  area?: 'app' | 'admin'
  /** admins outside the admin name: the admin console's base URL (admin pages open there) */
  admin_url?: string
  /** on the admin name: the cabinet's base URL */
  app_url?: string
  /** the plan (BILL-1, docs/billing/design.md §6); null with billing off (the box); only in GET /api/me and login */
  plan?: MePlan | null
  /** limits by pricing.json limit key (hours, days, seconds, an ingest preset); null = no limit */
  limits?: Record<string, number | string | null>
  /** account marks: vip = an ambassador of the service */
  badges?: 'vip'[]
  /** the same answer as GET /api/bridge/billing; null with billing off (BILL-APP-API) */
  billing?: BillingView | null
  /** the Strevix mark of the free plan (WM, ADR-0005 W1–W10); absent with billing off (the box) or an older center */
  watermark?: MeWatermark
}

/** GET /api/me watermark (WM2b ↔ WM3 contract). */
export interface MeWatermark {
  /** the account's plan puts the Strevix mark on its outputs (no `no_watermark` feature: free, and trial until billing adds it) */
  plan: boolean
  /** the center's switch WATERMARK: false = no account gets the mark yet (the flag is off by default) */
  enabled: boolean
  /** what a marked output sends: "720p30" */
  output: string
}

/** Why a broadcast has (or has not) the Strevix mark (watermark_effective.reason). */
export type WatermarkReason = 'plan' | 'no_watermark' | 'off' | 'node_unavailable'
/** node_unavailable in detail: the job is starting, no transcoder can blend, the transcoder went away, an output node without relay. */
export type WatermarkDetail = '' | 'starting' | 'no_blend_node' | 'transcoder_lost' | 'no_relay'

/** The stream's Strevix mark right now (InputView and AdminStream watermark_effective; WM2b ↔ WM3 contract). */
export interface WatermarkEffective {
  /** the mark is burned in on the broadcast's outputs */
  on: boolean
  /** plan: on by the plan; no_watermark: the plan goes without it; off: the flag is off; node_unavailable: due, but no transcoder makes it */
  reason: WatermarkReason
  detail: WatermarkDetail
  /** since when this state holds */
  since: string
}

export interface MePlan {
  id: string
  name: string
  /** subscription: the trial or a paid plan; free: the floor of the service; grant: given by an admin (a gift, legacy, an ambassador: told apart by id) */
  source: 'subscription' | 'free' | 'grant'
  /** absent without a subscription */
  status?: '' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'trial_expired' | 'paused'
  access: 'full' | 'no_new_streams'
  access_reason?: 'account_frozen' | 'account_streams_blocked' | 'plan_payment_disputed' | 'plan_paused'
  /** an ambassador of the service (badge vip) */
  vip: boolean
  /** when a granted plan ends */
  until?: string
  trial_ends_at?: string
  period_start?: string
  period_end?: string
  /** the subscription ends at period_end (Stripe, BILL-B4) */
  cancel_at_period_end: boolean
  /** only on free after another plan: what ended, when and why (BILL-APP-API) */
  previous_plan?: PreviousPlan
  features: string[]
}

export interface PreviousPlan {
  id: string
  name: string
  ended_at: string
  reason: 'trial_ended' | 'grant_ended' | 'legacy_ended' | 'canceled' | 'payment_failed'
}

/** GET /api/me billing = GET /api/bridge/billing billing (BILL-APP-API, docs/bridge-auth.md §18; strevix-clientkit appauth.Billing). */
export interface BillingView {
  plan: Omit<MePlan, 'features'>
  /** the checks that refuse now (ENTITLEMENTS_ENFORCE); absent = they only observe */
  enforced?: string[]
  quotas?: BillingQuota[]
  limits: BillingLimits
  features?: string[]
  links: { manage?: string; pricing?: string }
}

export interface BillingQuota {
  key: 'stream_hours_month' | 'captions_hours_month' | 'transcode_hours_month' | 'translation_lang_hours_month'
  unit: 'h'
  /** whole hours; null = no limit; 0 = not on the plan */
  limit: number | null
  /** hours with one decimal; absent when not metered or not on the plan */
  used?: number
  metered: boolean
  /** metered quotas only */
  hard?: boolean
  percent?: number
  level?: 'ok' | 'warn80' | 'full'
  resets_at?: string
  overrun?: { active: boolean; until?: string }
}

export interface BillingLimits {
  destinations: { used?: number; limit: number | null }
  concurrent_streams: { used?: number; limit: number | null }
  video_tracks_max: number | null
  audio_tracks_max: number | null
  source_grace_max_s: number | null
  ingest_max: string | null
}

export interface UserFeatures {
  /** bitrate fill of destinations (docs/bitrate-fill.md §4): fill fields in Destination, the editor's bitrate choice */
  fill?: boolean
  /** captions in the speaker's language (docs/captions-overlay.md §10.6): the Captions panel, Send captions of YouTube outputs */
  captions?: boolean
  /** translated captions (CAP-8, docs/captions-translation.md): Translate to in the panel, a YouTube output's captions language */
  caption_translation?: boolean
  /** the cabinet may be in Russian (LEGAL: only under this flag an admin turns on; BILL-B6a) */
  ru?: boolean
}

/*
 * Bitrate fill of outputs (docs/bitrate-fill.md §3, §4). source: the platform gets the encoder's
 * bitrate (its filler data back); none: the useful bytes only; floor: filler up to the profile's
 * threshold for the quality (auto: the input's resolution and frame rate) plus a margin.
 */
export type FillMode = 'source' | 'none' | 'floor'
export type FillQuality = 'auto' | '720p' | '1080p' | '1440p'
export type FillSource = 'destination' | 'profile' | 'platform'

/** The fill in force for a destination and where each value comes from. */
export interface DestFill {
  mode: FillMode
  quality: FillQuality
  mode_source: FillSource
  quality_source: FillSource
}

/** GET /api/admin/fill platform; PUT /api/admin/fill/platform {mode, quality, margin_pct}. */
export interface FillPlatform {
  mode: FillMode
  quality: FillQuality
  /** added to a threshold to get floor_kbps, 0…50 */
  margin_pct: number
  updated_by?: number
  updated_at?: string
}

/** A profile's default (null = the platform's) and its thresholds, kbit/s by class (720p30 … 1440p60). */
export interface FillProfile {
  profile: string
  mode: FillMode | null
  quality: FillQuality | null
  thresholds: Record<string, number>
  effective_mode: FillMode
  effective_quality: FillQuality
  updated_by?: number
  updated_at?: string
}

/** GET /api/admin/fill */
export interface FillSettings {
  platform: FillPlatform
  profiles: FillProfile[]
  modes: FillMode[]
  qualities: FillQuality[]
  classes: string[]
}

/** PUT /api/admin/fill/profiles/{profile}: absent fields stay, null mode / quality = the platform's. */
export interface FillProfileInput {
  mode?: FillMode | null
  quality?: FillQuality | null
  thresholds?: Record<string, number>
}

/** 200 of PATCH /api/admin/outputs/{id}/fill */
export interface OutputFillResult {
  output_id: string
  fill_mode: FillMode | null
  fill_quality: FillQuality | null
  fill_effective: DestFill
  applies: 'next_frame'
}

export type SetupState = 'pending' | 'completed' | 'dismissed'

/**
 * GET /api/ingest. rtmp / rtmps / ws are already the addresses of the chosen
 * region (or the automatic ones): the UI shows them as they are and never
 * builds them from regions[].
 */
export interface Ingest {
  rtmp: string
  rtmps?: string
  /** RTMP over WebSocket (Relay Link), when the node announces one */
  ws?: string
  /**
   * SRT address template without the secret (docs/srt-ingest.md §2): `{srt_id}` stands for
   * StreamKey.srt_id, the passphrase is the stream key. The full address is srt_url of
   * GET /api/stream-keys/{id}/reveal only. Absent when there is no SRT for these addresses.
   */
  srt?: string
  max_bitrate_kbps?: number
  /** encoder settings, not a region */
  recommended: string[]
  /** the user's choice; null = automatic */
  region: string | null
  /** regions with online nodes that accept ingest; empty = no choice to offer */
  regions: IngestRegion[]
  /** region whose addresses rtmp / rtmps / ws / srt above are, or 'auto' */
  recommended_region: string
  /**
   * Source grace of a stream key without its own (the center's SOURCE_GRACE), ms: "default: 10 s"
   * in the key settings (D21, 04-admin-api.md §6). Absent on an older center.
   */
  default_source_grace_ms?: number
  /**
   * The node a chosen region pins the user to, and where an own client goes when it fails
   * (C0-5, GROUP-2c). For apps only: the cabinet shows the region's addresses, never these hosts.
   */
  primary?: IngestNode
  backups?: IngestNode[]
  /** GROUP-DEFAULT (#550): the addresses are the automatic choice (no region chosen, or the chosen one is gone) */
  automatic?: boolean
  /**
   * GROUP-DEFAULT (owner V1, 01.11): the default node group behind the automatic region; the cabinet
   * names it on the Auto tile (NG-20 A). Absent: the center's addresses.
   */
  auto_group?: string
  /** GROUP-DEFAULT p. 2: the chosen region is gone; `to` is the node group its nodes went into, or "auto". */
  region_replaced?: { from: string; to: string }
}

/** The longest source grace a key or destination may have, ms (04-admin-api.md §6; the wire's bound). */
export const MAX_SOURCE_GRACE_MS = 600_000

export interface IngestRegion {
  region: string
  rtmp?: string
  rtmps?: string
  ws?: string
  /** SRT address template of the region (as Ingest.srt); absent = the region's node announces no SRT */
  srt?: string
  /** online nodes of the region (members of a node group); never shown to the user (owner, GROUP-UI) */
  nodes: number
  /** a node group (GROUP-2c): region is the group's name (us-east-1) and the addresses carry its DNS name */
  group?: boolean
}

/** Ingest.primary / backups[]: a node an own client (Studio, Bridge, OBS dock) connects to. Never shown in the UI. */
export interface IngestNode {
  host: string
  rtmp?: string
  rtmps?: string
  ws?: string
  srt?: string
}

/** PUT /api/me/ingest-region {region}: null = automatic. Answers with the new Ingest. */
export const AUTO_REGION = 'auto'

/** The media node a stream came in on, or an output runs on (managed nodes). */
export interface NodeRef {
  id: string
  name: string
  region: string
  /** the node group the node is a member of (us-east-1): the cabinet shows it instead of the node */
  group?: string
}

export type NodeRole = 'edge' | 'hub'
export type NodeEgress = 'direct' | 'via_center'
export type NodeStatus = 'online' | 'offline' | 'revoked'

/** GET /api/admin/nodes */
/** NodeInfo.drain (MOVE-1 B1; host commands drain / undrain) */
export interface NodeDrain {
  state: 'draining' | 'drained'
  since?: string
  deadline?: string
  /** what is left on the node (its own report only) */
  publishers?: number
  outputs?: number
  legs?: number
  /** publishers moved out at the deadline */
  moved?: number
  /** true: the node's report; false: the center's order only (offline, older, not taken yet) */
  reported: boolean
}

export interface NodeInfo {
  id: string
  name: string
  region: string
  role: NodeRole
  egress: NodeEgress
  /** false = drained: not offered in /api/ingest, running broadcasts continue */
  accept_ingest: boolean
  status: NodeStatus
  version?: string
  /** srt: the SRT listener the node announces, host:port (udp) */
  endpoints?: { rtmp?: string; rtmps?: string; ws?: string; srt?: string }
  created_at: string
  last_seen_at?: string
  revoked_at?: string
  connected_at?: string
  max_outputs?: number
  bandwidth_mbps?: number
  /** live streams placed on the node */
  streams: number
  /** outputs by state, from the node's stats */
  outputs: Record<string, number>
  /** bits/s to platforms between the last two stats reports */
  egress_bps?: number
  egress_bytes?: number
  link_bytes?: number
  stats_at?: string
  /** revision of the desired outputs last sent / acknowledged by the node */
  desired_rev?: number
  acked_rev?: number
  /** relay roles (routing network R0); role is derived from them. Absent from a center before R0.9b */
  roles?: RelayRole[]
  /** relay identity and peer list state (R0); absent from a center before R0.9b */
  relay?: NodeRelay
  load?: NodeLoad
  clock?: NodeClock
  /** R0.9h: the hosting provider, when known */
  provider?: string
  /** an online integrations node's leases (docs/integrations-nodes.md) */
  integrations?: { leases: number; lease_set_version: number; lease_set_acked: number; shard: boolean }
caps?: string[]
  /** drain of the node's process (MOVE-1): takes no new streams; absent = not draining */
  drain?: NodeDrain
  /** failover mode (C0-DNS-1 v2); absent = none: the node's name never moves */
  failover?: NodeFailover
  /** the node's address on its site's private network (GROUP-2c); absent = public only */
  private_addr?: string
}

export type FailoverMode = 'none' | 'backup' | 'group' | 'cluster'

/** NodeInfo.failover (C0-DNS-1 v2) */
export interface NodeFailover {
  mode: Exclude<FailoverMode, 'none'>
  /** g<id> (modes group, cluster) */
  group?: string
  group_name?: string
  /** n<id> (mode backup) */
  backup?: string
  backup_name?: string
  egress_point: 'center' | 'region_edge'
  /** egress_direct: the node's outputs die with it — a failure ends the platforms' broadcasts */
  warnings?: 'egress_direct'[]
  /** where the node's DNS name points now (the provisioner's health; C0-3/C0-4) */
  dns?: NodeDNS
  /** a node group member's state, weight and cap (mode group of a node group; GROUP-2a) */
  member?: GroupMember
}

export type MemberState = 'joining' | 'serving' | 'draining' | 'drained' | 'maintenance'

/** NodeFailover.member and GroupMemberView (node-group.md §4, §6.2) */
export interface GroupMember {
  /** '' = serving (a member from before GROUP-2a) */
  state: MemberState | ''
  state_since: string
  ramp_started_at?: string
  weight_mode: 'auto' | 'manual' | ''
  /** manual share of new connections, % */
  weight_pct?: number
  /** at most this share of connections, % */
  cap_pct?: number
}

/** A node group's parameters (node-group.md §6.5). */
export interface GroupParams {
  egress_offload: boolean
  auto_weights: boolean
  auto_ramp: boolean
  ramp_s: number
  balance_live_outputs: boolean
  balance_high: number
  balance_low: number
  balance_budget: number
  drain_deadline_s: number
}

export interface NodeDNS {
  state: 'serving' | 'failed_over' | 'recovering'
  since: string
  reason?: string
  order?: 'update' | 'drain'
  /** node ids the name points at */
  moved_to?: string[]
  hold_until?: string
  heartbeat: boolean
}

/** GET /api/admin/failover groups[] */
export interface FailoverGroup {
  id: string
  name: string
  kind: 'group' | 'cluster'
  backup?: string
  members: string[]
  /** the group's own DNS name, once the provisioner took the model */
  fqdn?: string
  created_at: string
  /** a node group named <region>-<index> (GROUP-2a); absent for a group named freely */
  region?: string
  index?: number
  params?: GroupParams
  /** the deployment's default node group, behind the automatic region (GROUP-DEFAULT #550) */
  default?: boolean
}

/** GET /api/admin/failover */
export interface FailoverState {
  groups: FailoverGroup[]
  nodes: Record<string, { mode: FailoverMode; group?: string; backup?: string; egress_point: string }>
  provisioner?: { at?: string; error?: string }
}

/** POST /api/admin/nodes/join-tokens body; expires_at only in the answer. */
export interface JoinToken {
  name: string
  region?: string
  role?: NodeRole
  egress?: NodeEgress
  /** ['integrations']: a token of an integrations node (no role or egress then). */
  roles?: string[]
  expires_at?: string
}

/** 201 of POST /api/admin/nodes/join-tokens. The token is shown only here: never cache or log it. */
export interface JoinTokenCreated {
  token: string
  join_token: JoinToken
}

/** PATCH /api/admin/nodes/{id} */
export interface NodePatch {
  name?: string
  region?: string
  role?: NodeRole
  egress?: NodeEgress
  accept_ingest?: boolean
}

/**
 * GET /downloads/manifest.json (docs/apps-downloads.md, section 2). Public,
 * served by Caddy next to the files; lists only builds that were actually
 * published, so the UI never mentions anything that is not in it.
 */
export interface AppsManifest {
  schema: number
  version: string
  released_at: string
  notes_url?: string
  apps: AppEntry[]
}

export interface AppEntry {
  id: string
  name: string
  summary: string
  /** menu group subtitle: "stream without OBS" */
  tagline?: string
  /** card text on /apps; summary when absent */
  description?: string
  /** install steps on /apps; a step with os is shown only if a file for that os exists */
  install?: { os?: string; text: string }[]
  /** plain note under the install steps */
  note?: string
  files: AppFile[]
}

export interface AppFile {
  /** macos | windows */
  os: string
  arch: string
  kind: string
  min_os?: string
  /** "macOS 11+, Apple Silicon or Intel"; built from min_os and arch when absent */
  requirement?: string
  url: string
  size: number
  sha256: string
  /** false: the UI shows first-run warnings for this file */
  signed: boolean
  /** "gui" (default) or "cli": a command line tool, shown with how to run it instead of install steps */
  variant?: string
}

export interface StreamKey {
  id: number
  stream_name: string
  label: string
  revoked_at?: string
  suspended_until?: string
  last_used_at?: string
  created_at: string
  /** public id of the key for SRT (the streamid, not a secret); kept on rotation, cleared on revocation */
  srt_id?: string
  user_id?: number
  /**
   * The key's source grace, ms (0–600 000): how long platforms stay connected after the encoder
   * left. null / absent = the center's default (Ingest.default_source_grace_ms).
   */
  source_grace_ms?: number | null
  /** the stream's captions: off, auto or a language of GET /api/captions (absent from older mocks = off) */
  captions_language?: string
  /** CAP-8: the languages its YouTube outputs may choose for translated captions */
  captions_translate?: string[]
  /**
   * Break screen (SLATE-1): when the video stops, viewers see a "be right back" screen until it is back or
   * slate_max_ms is up. On by default; after / max in ms, null = the default (5 s; as long as the hold).
   */
  slate_enabled?: boolean
  slate_after_ms?: number | null
  slate_max_ms?: number | null
  /**
   * E1 (docs/ertmp.md §6): the audio track with the streamer's voice (the OBS plugin's microphone, an AAC
   * track ≥ 1). Captions listen to it while it is live, else to track 0. null / absent = track 0.
   */
  voice_audio_track?: number | null
  /** VERT-1 (docs/vertical.md §2.4): main, or vertical (lists show main keys only, absent from older centers = main) */
  role?: 'main' | 'vertical'
  /** vertical: the id of its main key; null for a main key */
  pair_of?: number | null
  /**
   * VERT-1, lists only: the vertical input of this main key. null = never turned on; revoked_at set = turned off.
   * Absent from a center before VERT-1.
   */
  vertical?: VerticalKey | null
}

/** The vertical input of a main key as lists show it (never the secret: reveal with its id). */
export interface VerticalKey {
  id: number
  /** <main stream name>-v; never changes */
  stream_name: string
  srt_id?: string
  label: string
  created_at: string
  last_used_at?: string | null
  revoked_at?: string | null
}

/** PATCH /api/stream-keys/{id}: absent fields stay; source_grace_ms null = back to the default. 409 on a revoked key. */
export interface StreamKeyPatch {
  label?: string
  source_grace_ms?: number | null
  slate_enabled?: boolean
  slate_after_ms?: number | null
  slate_max_ms?: number | null
  /** E1: 0–255, null = track 0; applies from the next authorization of the stream */
  voice_audio_track?: number | null
}

export interface StreamKeyWithSecret extends StreamKey {
  key: string
  /**
   * reveal only: the full SRT address with the key as passphrase — as secret as the key.
   * Never cached (no TanStack Query), never stored, never logged; dropped as soon as it is hidden.
   */
  srt_url?: string
}

export interface Profile {
  id: string
  name: string
  url?: string
  key_help?: string
  /** public page that shows the stream key */
  key_url?: string
  /** 2–5 short steps to get the key */
  key_steps?: string[]
  /** without a key the destination is not sent to the node */
  needs_key: boolean
  /** a tile of the setup guide, in list order */
  featured?: boolean
  /** one key per live (instagram): after a finished broadcast that went live the key is removed and needs_key is set */
  key_per_session?: boolean
  /** the live does not start on the platform by itself: what to press there after OBS starts */
  go_live_note?: string
  /** transports the platform takes, the default first (youtube: rtmps, hls); absent = rtmps only */
  protocols?: ProfileProtocol[]
  /** E1h: the video codecs a destination with enhanced_codecs sends by RTMP(S); absent = H.264 only (youtube: h264, hevc, av1) */
  video_codecs?: VideoCodec[]
  /** ERTMP-CAT-1: the HEVC/AV1 checkbox shows Beta (our check on the platform has not passed); absent = no Beta */
  enhanced_codecs_beta?: boolean
  /** what the platform shows (BR2, docs/vertical.md §8): portrait platforms get the vertical input (input auto) */
  orientation?: 'landscape' | 'portrait' | 'both'
  /** how it takes both layouts at once */
  dual?: 'none' | 'twitch_multitrack' | 'youtube_second_key' | 'app_only'
  /** VERT-PLAT: the platform shows the server address with the key (tiktok): no default url, the user pastes both */
  server_from_platform?: boolean
}

export type VideoCodec = 'h264' | 'hevc' | 'av1'

export type DestProtocol = 'rtmps' | 'hls'

/** One transport of a profile (docs/youtube-hls.md §4). */
export interface ProfileProtocol {
  id: DestProtocol
  name: string
  /** server URL for this protocol: the center sets it when the protocol changes without a url */
  url: string
  /** what changes for the user with this protocol (EN; ES/RU in i18n.ts by protocol id) */
  hints?: string[]
  /** replace the profile's key_steps for this protocol */
  key_steps?: string[]
}

export interface Destination {
  id: number
  stream_key_id: number
  name: string
  profile: string
  url: string
  has_key: boolean
  /** the profile needs a key and there is none (never saved, removed, or spent by a finished live): not sent to the node */
  needs_key: boolean
  enabled: boolean
  output_id: string
  /** only with features.fill: the destination's own choice; null / absent = the profile's default */
  fill_mode?: FillMode | null
  fill_quality?: FillQuality | null
  /** only with features.fill: what is in force and where it comes from */
  fill_effective?: DestFill
  user_id?: number
  protocol?: DestProtocol
  /** the destination's own source grace, ms; null / absent = as its stream key's */
  source_grace_ms?: number | null
  /** youtube: the node sends the stream's captions to YouTube over HTTP */
  captions_http?: boolean
  /** a captions address from YouTube Studio is stored (the address itself never comes back) */
  has_captions_url?: boolean
  /** CAP-8: the language of its captions: '' the original, else a translation (its video then goes ~9–11 s later) */
  captions_lang?: string
  /** TRANS-1: off (default), auto or a preset id */
  transcode?: string
  /** TRANS-1: what its transcoding is right now (read-only) */
  transcode_effective?: TranscodeEffective
  /** CAPTIONS-DELIVERY-1: auto (the center picks the method) or off (no captions here) */
  captions_delivery?: 'auto' | 'off'
  /** CAPTIONS-DELIVERY-1: how its platform gets the captions now (read-only) */
  captions_delivery_effective?: CaptionsDelivery
  /**
   * E1 (docs/ertmp.md §5): the Enhanced RTMP video / audio track it gets (null / absent = 0, the default
   * track). While the track carries no media the output sends track 0 (state_reason track_missing).
   */
  video_track?: number | null
  audio_track?: number | null
  /** E1h (docs/ertmp.md §14): HEVC/AV1 tracks go to the platform as they are (default false); a change reconnects */
  enhanced_codecs?: boolean
  /** VERT-1 (docs/vertical.md §3.1): which input of the broadcast it sends; absent from older centers = main */
  input?: DestInput
  /** VERT-1 (§3.2): input vertical/auto, while the vertical input is not on air: main (default) or slate (wait; 9:16 break screen) */
  fallback?: DestFallback
  /** VERT-1 (§4.3): YouTube Dual stream — the horizontal destination of this vertical one */
  dual_of?: number | null
  /** VERT-1: what it sends now (read-only) */
  input_effective?: InputEffective
}

/** VERT-1: which input of the broadcast a destination sends (vertical: the vertical input; auto: vertical for portrait platforms). */
export type DestInput = 'main' | 'vertical' | 'auto'
export type DestFallback = 'main' | 'slate'

/** POST /api/destinations/youtube-dual (VERT-1, docs/vertical.md §4.1): both YouTube keys are write-only. */
export interface YouTubeDualInput {
  name?: string
  stream_key_id?: number
  main_key: string
  vertical_key: string
  enabled?: boolean
}

/**
 * CAPTIONS-DELIVERY-1 (docs/captions-delivery.md): how a destination gets the stream's captions.
 * http: the platform's captions API (YouTube); cea608: CC in the video (Twitch, Facebook; EN/ES);
 * burn: drawn into the video by the transcoder (viewers cannot hide them; transcoding hours).
 */
export interface CaptionsDelivery {
  method: '' | 'http' | 'cea608' | 'burn' | 'off'
  lang?: string
  translation?: boolean
  delay_ms: number
  state: 'on' | 'off' | 'waiting_captions' | 'unavailable' | 'plan' | 'node_unsupported'
  /** user_off, stream_captions_off, language_detecting, plan_no_captions, plan_no_caption_translation, translation_not_selected,
   *  http_not_set_up, cc608_not_ready, burn_not_ready, no_transcoder, plan_limit_transcode_hours, node_no_cc608 */
  reason?: string
  viewer_can_hide: boolean
  uses_transcode_hours?: boolean
}

/** TRANS-1 (docs/architecture/transcoder.md §8.1): a video's size, frame rate and bitrate. */
export interface TranscodeVideo {
  w: number
  h: number
  fps: number
  kbps: number
}

export type TranscodeState = 'off' | 'passthrough' | 'starting' | 'running' | 'fallback_source' | 'plan_limit'

/** TRANS-1: the destination's transcoding right now (destinations' transcode_effective). */
export interface TranscodeEffective {
  mode: string
  preset?: string
  decision?: 'passthrough' | 'transcode' | 'unknown'
  reasons?: string[]
  hints?: string[]
  input?: TranscodeVideo
  output?: TranscodeVideo
  state: TranscodeState
  /** no_transcoder, relay_off, egress_unknown, no_preset, transcoder_lost, plan_no_transcode, plan_limit_transcode_hours, plan_limit_transcode_outputs */
  reason?: string
  node_id?: string
  derived?: string
  /** WM: this output gets the copy with the Strevix mark (an output node without relay does not) */
  watermark?: boolean
}

/** hello.transcoder of a transcoder node */
export interface TranscoderEngine {
  engine: string
  sdk?: string
  gpu?: string
  driver?: string
  max_jobs: number
  up: boolean
  /** WM: what the engine can do; "blend" = it burns in the Strevix mark */
  caps?: string[]
}

/** a job in the transcoder node's stats */
export interface TranscodeNodeJob {
  job_id: string
  state: 'starting' | 'running' | 'no_source' | 'failed'
  speed_milli: number
  in_frames: number
  out_frames: number
  dropped: number
  lat_p95_us: number
  restarts: number
  error?: string
}

/** GET /api/admin/transcoders */
export interface TranscoderInfo {
  node_id: string
  name?: string
  region?: string
  online: boolean
  engine?: TranscoderEngine
  max_jobs: number
  jobs: number
  saturated: boolean
  stats?: { engine?: TranscoderEngine; jobs: TranscodeNodeJob[] }
}

/** GET /api/admin/transcode/jobs */
export interface TranscodeJobAdmin {
  id: string
  stream: string
  preset: string
  derived: string
  user_id?: number
  node_id: string
  src_node_id: string
  egress_node_id: string
  route_a: string
  route_b: string
  /** WM: transcode (a platform preset), burn (captions in the picture), watermark (the free plan's mark, <stream>.xfree.wm) */
  kind?: 'transcode' | 'burn' | 'watermark'
  state: 'starting' | 'running' | 'ended'
  target: { w: number; h: number; fps_num: number; fps_den: number; kbps: number; gop_ms: number; profile: string; bframes: number; nvenc_preset?: number; tuning?: string; follow_idr?: boolean }
  node_status?: TranscodeNodeJob
}

/** GET /api/admin/watermark (WM2b): the free-tier Strevix mark across the service — the one source of the count of free broadcasts without the mark (the same numbers as controlapi_watermark_free_broadcasts). */
export interface AdminWatermark {
  /** WATERMARK=on in the center and billing on (the box never shows the mark) */
  enabled: boolean
  output: '720p30'
  free_with_mark: number
  free_without_mark: number
}

export interface DestinationInput {
  name?: string
  profile?: string
  /** one of the profile's protocols; a change without url takes the protocol's URL and reconnects the output */
  protocol?: DestProtocol
  url?: string
  /** write-only; '' removes the key, so omit the field to keep it */
  key?: string
  enabled?: boolean
  /** create only: 'setup' returns an existing destination of the profile (200) instead of a duplicate */
  source?: 'setup'
  /** TRANS-1: off | auto | a preset id; applies at the next keyframe, no reconnect */
  transcode?: string
  /** only with features.fill (else 403); null = the default. Applies from the next frame, no reconnect */
  fill_mode?: FillMode | null
  fill_quality?: FillQuality | null
  /** 0–600 000 ms; null = as the stream key's. Applies from the next desired, no reconnect */
  source_grace_ms?: number | null
  /** youtube only; true needs features.captions */
  captions_http?: boolean
  /** write-only captions address from YouTube Studio; '' removes it, omit to keep it */
  captions_url?: string
  /** CAP-8: '' the original, or a translation language (needs features.caption_translation) */
  captions_lang?: string
  /** CAPTIONS-DELIVERY-1: auto or off (422 for anything else) */
  captions_delivery?: 'auto' | 'off'
  /** E1: the Enhanced RTMP tracks this destination gets (0–255, null = 0); switches at the next keyframe, no reconnect */
  video_track?: number | null
  audio_track?: number | null
  /** E1h: HEVC/AV1 tracks as they are (profiles with video_codecs beyond H.264, protocol rtmps); a change reconnects; 400 otherwise */
  enhanced_codecs?: boolean
  /** VERT-1: which input it sends; 422 vertical_no_transcode with transcode on */
  input?: DestInput
  /** VERT-1: while the vertical input is not on air */
  fallback?: DestFallback
}

export type Health = 'good' | 'warning' | 'bad' | 'offline'

/** What the signed downloads manifest says about the user's app (docs/apps-updates.md). */
export interface AppStatus {
  id: 'relay-link' | 'relay-studio' | 'strevix-obs'
  name?: string
  version: string
  latest?: string
  /** a newer version is published */
  newer: boolean
  min_version?: string
  /** below min_version */
  required: boolean
  enforce_after?: string
  /** nodes refuse this version now */
  enforced: boolean
  /** the cabinet's download page */
  download?: string
}

export interface InputView {
  stream_name: string
  /** VERT-1: vertical = the vertical input of the main stream pair_of (absent from older centers = main) */
  role?: 'main' | 'vertical'
  /** VERT-1: the main stream of a vertical input */
  pair_of?: string
  /**
   * VERT-1 (docs/vertical.md §5.2): the change of the pair's offset since 10 s after the vertical input came, ms.
   * The node's status has it (internal/api InputStatus); the center's live view does not pass it on yet
   * (question to VERT-LEAD in claude/vert-v4-report.md): the UI shows "—" while it is absent.
   */
  pair_drift_ms?: number
  live: boolean
  since?: string
  /** rtmp, rtmps or srt */
  protocol?: string
  encoder?: string
  /** our app that carried the stream ("relaylink/0.6.0": Strevix Bridge over wss) */
  via?: string
  /** our app's version against the manifest; absent for other encoders */
  app?: AppStatus
  health: Health
  reasons?: string[]
  bitrate_kbps: number
  fps: number
  declared_fps?: number
  gop_seconds: number
  resolution?: string
  video_profile?: string
  audio_format?: string
  max_bitrate_kbps?: number
  last_end_reason?: string
  last_error?: string
  /** video + audio bytes of the current broadcast (OBS reconnect within 10 s continues it) */
  bytes_received?: number
  /** publisher reconnects in the current broadcast */
  reconnects?: number
  /** arrival jitter, worst of video and audio */
  jitter_ms?: number
  state_reason?: InputStateReason
  /** OBS → us, an estimate; absent unless live. Numbers change without input events: live value in sample.delay_ms */
  delay?: InputDelay
  /** live and the node holds a keyframe ≤ 30 s old: GET /api/live/preview can serve a frame */
  preview_available?: boolean
  /** the media node the stream came in on (managed nodes) */
  node?: NodeRef
  /** the encoder connection as the node sees it; for SRT the srt block (no input event of its own: sample.network.srt every 5 s) */
  network?: InputNetwork
  /** filler data (H.264 NAL 12) the node removed from the encoder's video over 5 s; bitrate_kbps is the useful bitrate without it */
  filler_kbps?: number
  /** the stream's captions; present when the account has captions and the stream's language is not off */
  captions?: CaptionsView
  /** viewers see the break screen (SLATE-1): why, since when, until when at the latest; absent while off */
  slate?: InputSlate
  /** E1 (docs/ertmp.md §7): the Enhanced RTMP tracks, track 0 first; absent for a single-track publish and while the node is unreachable */
  tracks?: InputTrack[]
  /** E1: what the plan says about the live tracks — warnings only, nothing is refused */
  track_warnings?: InputTrackWarning[]
  /** WM: the free plan's Strevix mark on this broadcast; absent from an older center */
  watermark_effective?: WatermarkEffective
}

export type InputTrackWarning = 'track_vertical_not_in_plan' | 'track_over_ingest_max'

/** One Enhanced RTMP track of an input (E1). */
export interface InputTrack {
  /** E-RTMP trackId; 0 is the default track */
  index: number
  kind: 'video' | 'audio'
  /** FourCC: avc1, hvc1, av01, vp09, mp4a, Opus, fLaC, ac-3, ec-3, .mp3 */
  codec: string
  /** video: from the decoder config; absent when unknown */
  width?: number
  height?: number
  /** video: measured */
  fps?: number
  kbps: number
  /** ended: a SequenceEnd, or no packet for 5 s */
  state: 'live' | 'ended'
  last_seen?: string
}

/** source_left: OBS left; source_stalled: OBS stays connected without video. */
export interface InputSlate {
  reason: 'source_left' | 'source_stalled'
  since: string
  until: string
}

export type CaptionsState = 'off' | 'no_worker' | 'starting' | 'live' | 'stalled' | 'rejected'
export type CaptionsReason =
  | 'no_destination'
  | 'stream_offline'
  | 'no_slots'
  | 'node_no_endpoint'
  | 'node_no_captions'
  | 'worker_lost'
  | 'worker_failed'
  | 'status_timeout'
  | 'platform_rejected'
  // the plan refused this broadcast's captions (BILL-1 B3a); the stream goes on. Text: billing.ts refusalText
  | 'plan_no_captions'
  | 'plan_limit_captions_hours'
  | 'plan_captions_overrun'

/** InputView.captions (docs/captions-overlay.md §10.6). */
export interface CaptionsView {
  state: CaptionsState
  reason?: CaptionsReason
  /** the stream's setting: auto, en, … */
  language: string
  /** what the worker hears (with auto: detected) */
  detected_language?: string
  language_locked?: boolean
  /** end of a word → accepted by YouTube (else the worker's own lag) */
  lag_ms?: number
  /** CAP-8: the translation, while an output asks for one */
  translation?: CaptionsTranslationView
  /** CAP-8: the last phrases by channel (captions/<lang> the original, translation/<lang> a translation), oldest first */
  recent?: Record<string, string[]>
}

export type TranslationState = 'off' | 'starting' | 'live' | 'degraded' | 'unavailable' | 'no_capacity'

/** CaptionsView.translation (docs/captions-translation.md §9) */
export interface CaptionsTranslationView {
  state: TranslationState
  reason?: 'plan_no_caption_translation' | 'worker_no_translation' | 'no_capacity' | 'translation_unavailable'
  languages: string[]
  targets?: Record<string, { p90_ms?: number; late?: number }>
}

/** OutputView.captions: the node's captions sender of a YouTube destination. */
export interface OutputCaptionsView {
  /** the node's state; captions_rejected: YouTube refuses them */
  state: string
  lag_ms?: number
  last_post_at?: string
}

/** GET /api/captions */
export interface CaptionsInfo {
  available: boolean
  languages: string[]
  /** CAP-8: translated captions (absent from older servers) */
  translation?: { available: boolean; languages: string[] }
}

export interface CaptionsWorker {
  id: string
  name: string
  version: string
  gpu: string
  slots: number
  models: string[]
  languages: string[]
  created_at: string
  last_seen_at?: string
  revoked_at?: string
  /** the external service an API worker named in its last hello */
  provider?: CaptionsProvider
  /** the key the admin gave it (k<n>) */
  api_key_id?: string
  /** the machine cost account paying for its connected hours (ca<n>) */
  cost_account_id?: string
}

/** external recognition services of API workers */
export type CaptionsProvider = 'assemblyai' | 'soniox' | 'deepgram' | 'openai'

/** GET /api/admin/captions/api-keys: a key as the console sees it (never the key itself) */
export interface CaptionsAPIKey {
  id: string
  provider: CaptionsProvider
  label: string
  /** the last characters, to tell keys apart */
  last4: string
  created_at: string
  /** workers using it (w<n>) */
  used_by: string[]
  /** the api cost account paying for its audio (ca<n>) */
  account_id?: string
}

/**
 * Our own costs of the captions nodes (docs/captions.md «Учёт затрат»), not the users' billing.
 * Amounts are millionths of a dollar; days and months UTC.
 */
export type CostKind = 'api' | 'machine'
export type CostProvider = CaptionsProvider | 'vultr' | 'scaleway' | 'other'
export type CostState = 'ok' | 'warn' | 'over' | 'stopped'
export interface CostAccount {
  id: string
  name: string
  kind: CostKind
  provider: CostProvider
  currency: 'USD'
  /** per hour of audio (api) or of a machine (machine) */
  price_micros: number
  /** per calendar month; null: none */
  budget_micros: number | null
  over_budget: 'warn' | 'stop'
  created_at: string
  today_seconds: number
  today_micros: number
  month_seconds: number
  month_micros: number
  /** the month at this month's pace; null on its first day */
  forecast_micros: number | null
  state: CostState
  keys: string[]
  workers: string[]
}
export interface CostAccountInput {
  name: string
  kind?: CostKind
  provider?: CostProvider
  price_micros: number
  budget_micros: number | null
  over_budget: 'warn' | 'stop'
}
export interface CostsInfo {
  accounts: CostAccount[]
  now: string
  month_start: string
  warn_share: number
}
/** Scheduled captions machines (docs/captions.md «Машины по расписанию»). */
export type CloudProvider = 'vultr' | 'scaleway'
export interface CloudKey {
  id: string
  provider: CloudProvider
  label: string
  last4: string
  project_id?: string
  created_at: string
  /** pools using it (p<n>) */
  used_by: string[]
}
export interface CloudRegion { id: string; name: string }
export interface CloudPlan { id: string; gpu: string; vram_gb: number; vcpu: number; ram_mb: number; disk_gb: number; hourly: number; currency: 'USD' | 'EUR' }
export interface VMWindow { days: number[]; from: string; to: string }
export type VMState = 'creating' | 'booting' | 'ready' | 'draining' | 'deleting' | 'deleted' | 'failed'
export type VMKind = 'captions' | 'transcoder'
export interface CaptionsVM {
  id: string
  /** captions: a captions worker; transcoder: a transcoder node (TRANS-1 T6) */
  kind?: VMKind
  provider_id?: string
  worker_id?: string
  /** a transcoder machine's node once it joined (n<id>) */
  node_id?: string
  state: VMState
  phase?: string
  ip?: string
  from_snapshot: boolean
  last_error?: string
  created_at: string
  ready_at?: string
  drain_at?: string
  deleted_at?: string
}
export interface VMPoolInput {
  name: string
  cloud_key_id: string
  region: string
  plan: string
  count: number
  slots: number
  windows: VMWindow[]
  tz: string
  lead_minutes: number
  max_hours: number
  enabled: boolean
  account_id: string | null
  /** captions (default) or transcoder; fixed once the pool exists */
  kind?: VMKind
  /** transcoder pools: start machines when a job finds no transcoder node */
  on_demand?: boolean
  /** transcoder pools: the region its nodes join with ('' = the center's default) */
  node_region?: string
}
export interface VMPool extends Omit<VMPoolInput, 'account_id'> {
  id: string
  provider: CloudProvider
  account_id?: string
  snapshot_id?: string
  snapshot_state: 'none' | 'creating' | 'ready' | 'failed' | 'unsupported' | 'skipped'
  snapshot_at?: string
  manual_until?: string
  failures: number
  paused_reason?: string
  created_at: string
  vms: CaptionsVM[]
  /** machines it wants now and why: window, manual, on_demand, paused, budget, no_center, '' */
  want: number
  why: '' | 'window' | 'manual' | 'on_demand' | 'paused' | 'budget' | 'no_center'
  next_start?: string
  next_end?: string
}
export interface VMPoolsInfo {
  pools: VMPool[]
  /** the center has a public name the machines can dial */
  center: boolean
  max_machines: number
}

export interface CostDay {
  day: string
  seconds: number
  micros: number
}

export interface CaptionsJobAdmin {
  job_id: string
  stream: string
  node: string
  language: string
  state: Exclude<CaptionsState, 'off'>
  detected_language?: string
  rtf?: number
  lag_ms?: number
  last_error?: string
  /** the job's traffic since it started: audio in, captions out (an older worker: absent) */
  rx_bytes?: number
  tx_bytes?: number
  /** API jobs: seconds of audio sent to the service since the job started */
  billed_s?: number
}

/** where a worker recognizes speech (hello.kind) */
export type CaptionsWorkerKind = 'gpu' | 'cpu' | 'api'

/** a model a worker can use (hello.model_info) */
export interface CaptionsModelInfo {
  name: string
  type: 'asr' | 'vad' | 'lid' | 'second_pass' | 'mt' | 'other'
  device?: 'gpu' | 'cpu' | 'api'
  /** weights on disk, MB; absent = unknown or an external service */
  size_mb?: number
  /** usable now: weights on the worker's disk, or the service configured */
  ready: boolean
}

/** the worker's machine as its last status said; absent values the worker could not read */
export interface CaptionsResources {
  cpu_pct?: number
  ram_used_mb?: number
  ram_total_mb?: number
  gpu_pct?: number
  gpu_mem_used_mb?: number
  gpu_mem_mb?: number
  gpu_temp_c?: number
  rx_bytes?: number
  tx_bytes?: number
}

/** one 5 s point of a worker's machine */
export interface WorkerSample {
  t: string
  cpu_pct?: number
  ram_used_mb?: number
  gpu_pct?: number
  gpu_mem_used_mb?: number
  rx_bps?: number
  tx_bps?: number
}

/** GET /api/admin/captions/workers */
export interface CaptionsWorkerInfo extends CaptionsWorker {
  connected: boolean
  connected_since?: string
  jobs: CaptionsJobAdmin[]
  /** highest real-time factor of its live jobs (below 1 keeps up) */
  rtf?: number
  /** while connected (Network › Captions, owner 21.10); an older worker sends none of these */
  kind?: CaptionsWorkerKind
  host?: { cpu_cores?: number; ram_mb?: number; gpu_mem_mb?: number; os?: string }
  model_info?: CaptionsModelInfo[]
  res?: CaptionsResources & { at: string; rx_bps?: number; tx_bps?: number }
  history?: WorkerSample[]
  /** an API worker has its service's key now; without it the worker takes no jobs */
  key_ready?: boolean
  /** its api or machine cost account is over its budget with action stop: no new jobs */
  cost_stopped?: boolean
  /** what it cost today and this month (UTC), all its accounts */
  cost?: { today_micros: number; month_micros: number }
}

/** POST /api/admin/captions/workers: the token is shown only now */
export interface NewCaptionsWorker extends CaptionsWorker {
  token: string
}

/** Network of an input (Network in the contract): only what the UI reads. */
export interface InputNetwork {
  rtt_ms?: number
  srt?: SrtNetwork
}

/**
 * SRT input over the 5 s window (docs/srt-ingest.md §3). Shown neutrally: a problem of the
 * input is only what state_reason / health already say (measure 4).
 */
export interface SrtNetwork {
  rtt_ms?: number
  /** negotiated TSBPD latency */
  latency_ms?: number
  /** data waiting for its TSBPD time */
  recv_buf_ms?: number
  /** packets detected missing (NAKed) in the window */
  pkt_loss_win?: number
  /** retransmitted packets received in the window */
  pkt_retrans_win?: number
  /** packets that never arrived in time */
  pkt_drop_win?: number
  bw_kbps?: number
  cipher?: string
}

/** OBS → us: to_us_ms = rtt_ms / 2 + backlog_ms (RTMP has no wall-clock time, hence estimated). */
export interface InputDelay {
  to_us_ms: number
  rtt_ms: number
  /** encoder data behind its own timestamps (waiting in its send buffer) */
  backlog_ms: number
  estimated: true
}

export type DelayKind = 'full' | 'ours' | 'none'

/**
 * Delay of a line, computed on the server. to_platform_ms = lag + send queue + rtt / 2.
 * total_ms (what the chip shows): ours — OBS → our server (= to_us_ms); full — OBS → viewers
 * (to_us_ms + to_platform_ms + platform_to_viewers_ms). Absent for none.
 * kind / warning changes send an output event; the numbers come in sample.delay_ms.
 */
export interface OutputDelay {
  to_platform_ms: number
  platform_to_viewers_ms?: number
  total_ms?: number
  kind: DelayKind
  latency_mode?: string
  /** yellow chip: the stream suffers — the output's state_reason is slow_link / lagging */
  warning: boolean
  /** information, shown neutrally: > 2× the line's 5-minute median (by > 200 ms); optional for older servers */
  above_median?: boolean
}

/**
 * TCP_INFO of the node → platform connection (diagnostics, shown neutrally). Late acks,
 * retransmissions and RTT swings are normal on long routes and do not hurt the stream by themselves.
 */
export interface LinkNetwork {
  rtt_ms: number
  rtt_var_ms?: number
  min_rtt_ms?: number
  retrans_pct?: number
  lost_segments?: number
  /** congestion control (bbr, cubic), when the node reports it */
  cc?: string
}

/**
 * GET /api/live/preview?stream=&format= — the stream's newest keyframe, decoded in the browser.
 * annexb (default): Annex B for WebCodecs; fmp4: one fMP4 fragment for MSE.
 * 200 headers: X-Codec, X-Width, X-Height, X-Frame-Age-Ms. Errors are JSON { error: PreviewError }:
 * 404 offline / no_keyframe, 429 rate_limited (+ Retry-After, s), 502 node_unreachable.
 */
export type PreviewFormat = 'annexb' | 'fmp4'
export type PreviewError = 'offline' | 'no_keyframe' | 'rate_limited' | 'node_unreachable'
export const previewPath = (stream: string, format: PreviewFormat = 'annexb') =>
  `/api/live/preview?stream=${encodeURIComponent(stream)}&format=${format}`

/** Why an input is not live or not stable; absent = live and fine. */
export type InputStateReason =
  | 'source_offline'
  | 'key_revoked'
  | 'stream_suspended'
  | 'account_blocked'
  | 'codec_unsupported'
  | 'bitrate_over_limit'
  | 'unstable'
  /** the node does not answer: `live` is the last known value, measurements are absent */
  | 'node_unreachable'
  /** another stream of the account is on another ingest node (docs/vertical.md §2.2) */
  | 'ingest_mismatch'
  /*
   * Refusals by the plan or an account hold (BILL-1 design §4.3; Hub.SetRefusal from authorize):
   * the center sends them as the input's state_reason (docs/controlapi.yaml enum since BILL-B2F).
   * On a live input: let in without outputs by a node without policy_refuse/1 (docs/billing/rollout.md).
   * Texts: billing.ts refusalText.
   */
  | 'plan_limit_concurrent'
  | 'plan_limit_stream_hours'
  | 'plan_paused'
  | 'plan_payment_disputed'
  | 'plan_no_vertical'
  | 'account_frozen'
  | 'account_streams_blocked'

/** Cause of an output state (docs/m15-live-api.md 2.1); absent = fine. Never parse last_error. */
export type OutputStateReason =
  | 'source_offline'
  /** waiting: enabled, but the platform needs a stream key and none is saved (not an error) */
  | 'needs_key'
  | 'platform_rejected_key'
  | 'platform_rejected'
  | 'platform_unreachable'
  | 'platform_tls'
  | 'platform_disconnected'
  | 'platform_stalled'
  | 'slow_link'
  /** live, protocol hls: the encoder's keyframes are over 5 s apart */
  | 'gop_too_long'
  | 'lagging'
  | 'node_unreachable'
  | 'internal'
  /** E1 (docs/ertmp.md §5.4, Q3): the selected track carries no media, the output sends the working track (track 0) */
  | 'track_missing'
  /** E1 (§5.2, Q9): the selected track's codec is not one the output can send */
  | 'track_codec_unsupported'

export type OutputState =
  | 'waiting'
  | 'starting'
  | 'live'
  | 'reconnecting'
  | 'failed'
  | 'stopped'
  | 'off'
  | 'pending'
  | 'stopping'
  | 'unknown'

export interface OutputView {
  destination_id: number
  output_id: string
  name: string
  profile: string
  enabled: boolean
  state: OutputState
  state_since?: string
  health: Health
  reasons?: string[]
  bitrate_kbps: number
  lag_ms: number
  reconnects: number
  live_seconds: number
  last_error?: string
  last_error_at?: string
  /** attempt in the current retry series (reconnecting, failed); diagnostics only */
  retry_attempt?: number
  /** end of the current pause between attempts (reconnecting, failed) */
  next_retry_at?: string
  state_reason?: OutputStateReason
  /** bytes sent since the node created the output; 0 again after the output is replaced */
  bytes_sent?: number
  /** diagnostics of the link to the platform (absent off Linux or when not connected) */
  network?: LinkNetwork
  /** RTMP: how late the platform acknowledges data (YouTube ~10 s as a rule); HLS: segment cut → accepted */
  ack_delay_ms?: number
  /** age of the oldest byte waiting in the node's send queue (HLS: oldest segment not yet accepted) */
  send_queue_ms?: number
  /** always sent by the API; optional so older mocks stay valid */
  delay?: OutputDelay
  /** what the platform reports (connected account); absent for custom RTMP */
  platform?: PlatformView
  /** filler the node added over 5 s; included in bitrate_kbps (what goes to the platform) */
  fill_kbps?: number
  /** the fill in force on the node; absent from a node without cap fill/1 */
  fill_mode?: FillMode
  /** the fill is off: a slow platform holds half the lag budget (back after 3 s of normal) */
  fill_suspended?: boolean
  /** the node's captions sender (YouTube with captions on) */
  captions?: OutputCaptionsView
  /** CAPTIONS-DELIVERY-1: how its platform gets the captions (destinations on air) */
  captions_delivery_effective?: CaptionsDelivery
  /** chat of the output (docs/chat.md); absent from a center without chats */
  chat?: OutputChat
  /** VERT-1: which input of the pair it sends (main | vertical | auto) and what it sends now */
  input?: DestInput
  input_effective?: InputEffective
}

/** What a destination sends now (VERT-1, read-only). */
export interface InputEffective {
  input: 'main' | 'vertical' | 'none'
  state: 'ok' | 'waiting_vertical' | 'fallback_main' | 'vertical_off'
  /** VERT-PLAT (owner 02.11): a portrait platform gets a landscape picture now; it goes as is, the cabinet warns */
  landscape?: boolean
}

/** OutputView.chat (docs/chat.md): whether the center reads this output's chat. */
export interface OutputChat {
  access: 'reads' | 'connect' | 'reconnect' | 'unsupported'
  /** with reads: waiting (output not live yet), live, reconnecting (platform connection down) */
  state?: 'waiting' | 'live' | 'reconnecting'
  /** other_channel, coming_later, not_configured, or another code */
  reason?: string
}

export type ChatPlatform = 'twitch' | 'kick'
export type ChatRole = 'owner' | 'mod' | 'vip' | 'member' | 'verified' | 'staff'
export type ChatKind = 'message' | 'paid' | 'membership' | 'raid' | 'announcement' | 'system'

/** One part of a chat message; the texts add up to ChatEvent.text. All strings are plain text. */
export interface ChatFragment {
  t: 'text' | 'emote' | 'mention' | 'link'
  text: string
  id?: string
  /** emote: image on the platform's CDN */
  img?: string
  user_id?: string
}

/** One item of a chat (GET /api/chat/events `chat`, chat_snapshot.events). */
export interface ChatEvent {
  id: string
  seq: number
  output_id: string
  destination_id: number
  platform: ChatPlatform | string
  channel: { id: string; name: string }
  at: string
  received_at: string
  kind: ChatKind | string
  author?: { id: string; name: string; roles?: (ChatRole | string)[]; badges?: { key: string; label?: string; img?: string }[]; color?: string }
  text: string
  fragments?: ChatFragment[]
  amount?: { display?: string; value?: number; unit?: string; tier?: string; months?: number; count?: number; viewers?: number } | null
  flags?: { first?: boolean; highlighted?: boolean }
  reply_to?: { id: string; author_name?: string } | null
}

/** chat_snapshot and GET /api/chat/recent: recent events, oldest first. */
export interface ChatSnapshot {
  cursor: string
  events: ChatEvent[]
}

/** chat_update: delete removes ids; ban removes the author's messages on the output; clear removes the output's chat. */
export interface ChatUpdate {
  op: 'delete' | 'ban' | 'clear' | string
  seq: number
  output_id: string
  platform: string
  channel_id: string
  ids?: string[]
  author_id?: string
  until?: string
}

export type PlatformLink = 'connected' | 'not_connected' | 'unsupported'

/**
 * "On {Platform}" block. stats/issues/updated_at only while live here and connected; stats absent
 * until the platform shows the stream live. stale: no answer for 90 s, numbers are the last known.
 */
export interface PlatformView {
  link: PlatformLink
  stats?: PlatformStats
  issues?: PlatformIssue[]
  updated_at?: string
  stale: boolean
}

/** v1.5: YouTube — viewers, likes; Twitch — viewers, uptime_s, new_followers. No chat_per_min. */
export interface PlatformStats {
  viewers: number
  likes?: number
  new_followers?: number
  uptime_s?: number
}

export interface PlatformIssue {
  /** YouTube configurationIssues type, or stream_not_found / other_channel */
  code: string
  /** shown as "{Platform} says: …" */
  text: string
  severity: 'warning' | 'error'
}

/** GET /api/platform-accounts */
export interface PlatformAccount {
  id: number
  platform: 'youtube' | 'twitch' | 'kick'
  external_id: string
  display_name: string
  scopes: string[]
  created_at: string
}
export interface PlatformAccounts {
  accounts: PlatformAccount[]
  /** configured integrations; others are unsupported */
  available: Array<'youtube' | 'twitch' | 'kick'>
}
/** POST /api/platform-accounts/{platform}/connect → open url in the same tab. Back on / with
 * ?platform_connected=<p> or ?platform_error=<p>&reason=denied|expired|failed|no_channel */
export type PlatformConnectError = 'denied' | 'expired' | 'failed' | 'no_channel'

/** One 5 s window of an output or input. */
export interface Sample {
  /** end of the window, aligned to 5 s */
  t: string
  bitrate_kbps: number
  /** worst in the window; offline only if the whole window was */
  health: Health
  /** worst in the window; any state other than live is worse ('unknown' while the node is unreachable) */
  state: string
  lag_ms: number
  /** sample event only: values at the end of the window */
  bytes_sent?: number
  bytes_received?: number
  /** inputs: frame rate at the end of the window (kept in the history) */
  fps?: number
  /** inputs: frames that did not arrive, % of the declared rate, mean over the window; absent when not live or none declared */
  drop_pct?: number
  /** SRT inputs: packets the receiver dropped as too late in the node's last 5 s window */
  pkt_drop?: number
  /** sample event only, inputs */
  jitter_ms?: number
  /** mean delay in the window: output delay.total_ms, input delay.to_us_ms; absent when not live */
  delay_ms?: number
  /** outputs: platform viewer count last reported in the window (connected account) */
  viewers?: number
  /** sample event only, SRT inputs: network.srt at the end of the window (the "SRT connection" block follows it) */
  network?: { srt?: SrtNetwork }
  /**
   * Not in the contract's Sample yet (docs/bitrate-fill-results.md, «Веб»): read when a server sends
   * them, otherwise the values of the last snapshot / output / input event stay.
   */
  fill_kbps?: number
  filler_kbps?: number
  /** admin only (StreamsHistory.paths): an output's path delay by segment, means over the window, ms */
  path?: PathSample
}

/** Sample.path (ADMIN-DELAY-1): the segments' means over a 5 s window, ms. */
export interface PathSample {
  obs_to_node_ms?: number
  node_ms?: number
  network_ms?: number
  to_platform_ms?: number
  total_ms?: number
  total_max_ms?: number
  clock_error_ms?: number
}

/** Last 5 minutes, old to new, up to 60 samples per object. */
export interface History {
  interval_s: number
  /** by output_id */
  outputs: Record<string, Sample[]>
  /** by stream_name */
  inputs: Record<string, Sample[]>
}

/** GET /api/admin/streams/history: the last 5 minutes of every input, by stream_name. */
export interface StreamsHistory {
  interval_s: number
  inputs: Record<string, Sample[]>
  /** the path delay by segment of every output that was live, by output_id (sample.path) */
  paths?: Record<string, Sample[]>
}

/** SSE `sample` event, every 5 s: the window that just closed. */
export interface SampleEvent {
  t: string
  outputs: Record<string, Sample>
  inputs: Record<string, Sample>
  /** as in UserView, at the end of the window */
  viewers_total?: number
  viewers_delta_5m?: number
}

export interface UserView {
  node: 'ok' | 'unreachable' | 'unknown'
  inputs: InputView[]
  outputs: OutputView[]
  /** absent until the API keeps history (GET /api/live/history reloads it) */
  history?: History
  /** watching on platforms that report it; absent when none does (no "Watching" metric then) */
  viewers_total?: number
  /** viewers_total minus ~5 minutes ago (▲/▼) */
  viewers_delta_5m?: number
}

export interface AdminUser extends User {
  stream_keys: number
  destinations: number
  destinations_enabled: number
  live: boolean
  last_live_at?: string
}

export interface AdminStream extends InputView {
  user_id: number
  email: string
  publisher?: string
  sessions: number
  takeovers: number
  outputs: number
  suspended_until?: string
  /** the line OBS → platform by segment for each live output (admin only) */
  path_delay?: PathDelay
}

/**
 * The line "frame left OBS → frame left for the platform" by segment, p50 of each, ms (ADMIN-DELAY-1,
 * docs/latency.md §2). Computed by the API; the console only shows it.
 */
export interface PathDelay {
  obs_to_node_ms?: number
  ingress_node?: NodeRef
  outputs: OutputPathDelay[]
  total_min_ms?: number
  total_max_ms?: number
  warning: boolean
}

export interface OutputPathDelay {
  output_id: string
  platform: string
  label: string
  egress_node?: NodeRef
  segments: {
    obs_to_node_ms?: number
    node_ms?: number
    node_p99_ms?: number
    node_bus_wait_ms?: number
    /** through the routing network only */
    network_ms?: number
    network_p99_ms?: number
    network_hops?: { from: NodeRef; to: NodeRef; cum_ms?: number }[]
    to_platform_ms?: number
    send_queue_ms?: number
    platform_rtt_ms?: number
  }
  total_ms?: number
  /** a segment is missing: total_ms is what is known */
  partial: boolean
  /** total_ms above 1 s */
  warning: boolean
  check_e2e_ms?: number
  e2e_mismatch?: boolean
  /** clock error inside network_ms, only above 10 ms or when a clock is suspect */
  clock?: { error_ms: number; suspect: boolean }
  by_design?: { srt_tsbpd_ms?: number; video_delay_ms?: number; hls?: boolean }
}

/** GET /api/admin/outputs/{id}/delay: the output's path delay from the database. */
export interface DelaySeries {
  output_id: string
  range: string
  step: '5s' | '1m' | '1h'
  from: string
  to: string
  points: (PathSample & { t: string; egress_node?: string; samples: number })[]
}

export interface AdminOutput extends OutputView {
  stream: string
  destination: string
  user_id: number
  email: string
  managed: boolean
  /** the media node running the output (managed nodes) */
  node?: NodeRef
  /**
   * The destination's own fill choice (null = the profile's) and what the center resolves. Proposed,
   * not in AdminOutput of the contract yet (fill_mode there is the node's mode in force; see
   * docs/bitrate-fill-results.md, «Веб»): the override dialog uses them when present.
   */
  fill_choice?: { mode: FillMode | null; quality: FillQuality | null }
  fill_effective?: DestFill
  /**
   * The encoder is away: the output holds its platform session until then (destination's grace,
   * else the key's, else SOURCE_GRACE; center clock). Absent while the stream is live.
   */
  source_grace_until?: string
  /** API-UI: the hold in force for this output (ms) and where it comes from */
  source_grace_ms?: number
  /** API-UI: own — the destination's; key — the stream key's; default — the center's SOURCE_GRACE */
  source_grace_source?: GraceSource
}

export type GraceSource = 'own' | 'key' | 'default'

export interface AuditEntry {
  id: number
  actor?: string
  user_id?: number
  user?: string
  action: string
  target: string
  details: Record<string, unknown>
  created_at: string
}

export interface Invite {
  id: number
  note: string
  used_email?: string
  used_at?: string
  expires_at: string
  created_at: string
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** the parsed JSON body of the error answer, when there was one */
    public data?: unknown,
  ) {
    super(message)
  }

  /** `code` of a coded error answer (HostError and the like), if any. */
  get code(): string | undefined {
    const c = (this.data as { code?: unknown } | undefined)?.code
    return typeof c === 'string' ? c : undefined
  }
}

/** The CSRF token of the session (cookie rs_csrf): every non-GET request carries it as X-CSRF-Token. */
export function csrf(): string {
  const m = document.cookie.match(/(?:^|;\s*)rs_csrf=([^;]+)/)
  return m?.[1] ? decodeURIComponent(m[1]) : ''
}

export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {}
  if (method !== 'GET') headers['X-CSRF-Token'] = csrf()
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (res.status === 204) return undefined as T
  const text = await res.text()
  let data: unknown = undefined
  try {
    data = text ? JSON.parse(text) : undefined
  } catch {
    // not JSON (proxy error page)
  }
  if (!res.ok) {
    const msg = (data as { error?: string } | undefined)?.error ?? `Request failed (${res.status})`
    throw new ApiError(res.status, msg, data)
  }
  return data as T
}

export const get = <T>(path: string) => api<T>('GET', path)
export const post = <T>(path: string, body?: unknown) => api<T>('POST', path, body)
export const patch = <T>(path: string, body?: unknown) => api<T>('PATCH', path, body)
export const put = <T>(path: string, body?: unknown) => api<T>('PUT', path, body)
export const del = <T>(path: string) => api<T>('DELETE', path)

/*
 * Hosts of the media nodes, their agents and first installs (M2.4,
 * docs/m2-provisioning.md; docs/controlapi.yaml, tag hosts). controlapi
 * proxies these to the provisioner. Errors are HostError: read `code`
 * (ApiError.code), never the text.
 */

/**
 * relay_key (routing network R0): the node's relay key is registered and every peer acknowledged the
 * peer list with it. Not in the provisioner's stages yet: the console shows it after join from the
 * node's relay state (Hosts.tsx, relayKeyStage) and takes the provisioner's own when it comes.
 */
export type InstallStageName = 'dns' | 'ssh_connect' | 'host_key' | 'preflight' | 'docker' | 'files' | 'install' | 'agent' | 'agent_online' | 'dns_ready' | 'join' | 'relay_key' | 'cleanup' | 'reboot' | 'after_reboot'
export const INSTALL_STAGE_ORDER: InstallStageName[] = ['dns', 'ssh_connect', 'host_key', 'preflight', 'docker', 'files', 'install', 'agent', 'agent_online', 'dns_ready', 'join', 'relay_key', 'cleanup', 'reboot', 'after_reboot']
export type InstallStageStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped'

export interface InstallStage {
  name: InstallStageName
  status: InstallStageStatus
  /** short; on failure the reason; host_key: the fingerprint */
  text?: string
  /** last 4 KB of the remote output, secrets masked */
  output?: string
  /** host_key accepted on first use: show the fingerprint prominently */
  tofu?: boolean
  started_at?: string
  finished_at?: string
}

export interface InstallJob {
  id: string
  host_id: string
  kind: 'install'
  state: 'running' | 'failed' | 'done'
  /** the current stage, or the one that failed (a retry resumes there) */
  stage: InstallStageName
  stages: InstallStage[]
  admin_id?: number
  params?: {
    address?: string
    ssh_port?: number
    ssh_user?: string
    ssh_host_key?: string
    slug?: string
    hostname?: string
    region_hint?: string
    keep_ssh_access?: boolean
    rebind?: boolean
    reboot?: RebootPlan
  }
  error?: string
  created_at?: string
  finished_at?: string
  release_no?: number
}

export type HostCommandKind =
  | 'status'
  | 'diagnostics'
  | 'restart'
  | 'os_update'
  | 'install_packages'
  | 'remove_ssh_access'
  | 'reboot'
  | 'reboot_cancel'
  | 'unenroll'
  | 'deploy'
  | 'redeploy'
  | 'rollback'
  | 'rebind'
  /** PROV-RELAY-1: RELAY in the node's .env; enable registers the relay key the agent reads (same node id) */
  | 'relay_enable'
  | 'relay_disable'
  | 'drain'
  | 'undrain'
  /** Planned restart of the release already installed. Not deploy, not a service restart. */
  | 'restart_node'
  /** HOLD-A8: {service: logic} — blue-green to the release's logic; {service: agent} — the agent alone */
  | 'update'

/** POST /api/admin/hosts/{id}/commands, relay_enable / relay_disable: what the center did with the agent's result */
export interface HostRelayResult {
  node_id: string
  enabled: boolean
  /** a first relay key registered now */
  registered?: boolean
  spki_prefix?: string
  error?: string
  /** spki_in_use | key_differs | bad_result | internal */
  code?: string
}

export type HostCommandState = 'sent' | 'ok' | 'failed' | 'timeout'

export interface HostCommand {
  seq: number
  kind: HostCommandKind
  admin_id?: number
  args?: Record<string, unknown>
  issued_at?: string
  /** timeout: no result within the kind's time limit yet; a late result turns it into ok or failed */
  state: HostCommandState
  error?: string
  /** per kind, see HostCommand.data in the contract */
  data?: Record<string, unknown>
  finished_at?: string
  /** reboot (and deploy with reboot): what ran on the node when it was ordered */
  live?: LiveCount
  /** POST answer of a deploy/redeploy with reboot: the reboot scheduled after it came up healthy */
  reboot?: HostCommand
  /** POST answer of relay_enable / relay_disable: the center's part (PROV-RELAY-1) */
  relay?: HostRelayResult
}

/** A reboot after a deploy or an install: none, in a minute, or later. */
export type RebootPlan = 'none' | 'now' | { delay_minutes: number }

export interface LiveCount {
  streams: number
  outputs: number
}

export type NetWarning = 'not_bbr' | 'no_fq' | 'queues' | 'time_not_synced' | 'docker_proxy'

/** HostStatus.network: docs/edge-node.md, «Сеть хоста». */
export interface HostNetwork {
  dev?: string
  cpus?: number
  host_cc?: string
  node_cc?: string
  node_slow_start_after_idle?: number
  node_mtu_probing?: number
  qdisc?: string
  fq?: boolean
  queues?: number
  queues_max?: number
  time_synced?: boolean
  softnet_dropped?: number
  softnet_time_squeeze?: number
  conntrack_invalid?: number
  docker_proxy_external?: number
  warnings?: NetWarning[]
}

/** data of the last successful status (Host.status). */
export interface HostStatus {
  host_id?: string
  agent_version?: string
  release_no?: number
  os?: string
  kernel?: string
  uptime_s?: number
  disk_free_mb?: number
  load1?: number
  boot_id?: string
  reboot_required?: boolean
  reboot_reasons?: string[]
  reboot_scheduled_at?: string
  containers?: Record<string, string>
  node_connected?: boolean
  network?: HostNetwork
}

export type RebootCheckName = 'agent' | 'containers' | 'node_online' | 'bbr' | 'fq' | 'reboot_flag'

/** The last reboot ordered from the center and the check after it (Host.reboot). */
export interface RebootWatch {
  seq: number
  admin_id?: number
  ordered_at?: string
  due_at?: string
  scheduled_at: string
  boot_id?: string
  state: 'scheduled' | 'checking' | 'ok' | 'failed' | 'cancelled'
  checks?: { name: RebootCheckName; ok: boolean; text?: string }[]
  error?: string
  finished_at?: string
}

/** GET /api/admin/hosts (without commands), GET /api/admin/hosts/{id} (with the last 100). */
export interface Host {
  id: string
  slug: string
  host_fingerprint?: string
  /** the agent is connected now */
  online: boolean
  /** the center's SSH key is still in the host's authorized_keys (backup channel) */
  ssh_access: boolean
  ssh_host_key?: string
  address?: string
  hostname?: string
  dns_managed?: boolean
  /** the media node it runs (GET /api/admin/nodes) */
  node_id?: string
  installing?: boolean
  agent_version?: string
  release_no?: number
  os?: string
  last_seen?: string
  last_seq?: number
  trust_seq?: number
  min_ops_serial?: number
  created_at?: string
  revoked_at?: string
  commands?: HostCommand[]
  /** the last status's data (network facts, reboot state) and when it came */
  status?: HostStatus
  status_at?: string
  reboot?: RebootWatch
  /** what the agent can do (its hello): update_logic, release_schema_2, update_agent (HOLD-A8) */
  caps?: string[]
  /** the node's layout (heartbeat over the last status); absent = not known yet */
  layout?: 'split' | 'monolith'
  /** split node: the active logic color and its generation */
  active_logic?: 'a' | 'b'
  generation?: number
  /** logic, ingress, egress, netns, caddy, agent (and logic-a, logic-b from the last status) */
  components?: Record<string, HostComponent>
  /** what the center's release changes on this node; [] nothing; absent = not known */
  changes?: ComponentName[]
}

/** A node component (HOLD-A8). */
export type ComponentName = 'logic' | 'ingress' | 'egress' | 'netns' | 'caddy' | 'agent'

export interface HostComponent {
  version?: string
  image?: string
  boundary?: string
  state?: string
  release_no?: number
}

/** A component of the center's node release (InstallInfo.components). */
export interface ReleaseComponent {
  image?: string
  version?: string
  boundary?: string | { min: string; max: string }
}

/** One step of the agent's logic switch (restart_node, update logic). */
export interface SwitchStep {
  name: 'checks' | 'start' | 'promote' | 'verify' | 'finish' | 'rollback' | string
  ms?: number
  ok: boolean
  error?: string
}

/** HostCommand.data of restart_node and update (the agent's result, as it came). */
export interface SwitchResult {
  service?: 'logic' | 'agent'
  release_no?: number
  logic_release_no?: number
  planned?: boolean
  from?: 'a' | 'b'
  to?: 'a' | 'b'
  generation?: number
  holders?: boolean
  sockets?: string
  streams?: number
  slots?: number
  /** only when the egress holder measured it — never 0 instead */
  pause_ms?: number
  pause_slot?: string
  logic_version?: string
  logic_tag?: string
  steps?: SwitchStep[]
  rolled_back?: boolean
  failed_step?: string
  code?: string
  reason?: string
  /** the rollback failed too */
  failed?: boolean
  warnings?: string[]
  /** update {service: agent} */
  version?: string
  agent_update?: boolean
  rollback_after_s?: number
}

/** What a first install will use (`install` in ProvisioningKeys). */
export interface InstallInfo {
  enabled: boolean
  /** free text: why installs are off (no release in the provisioner image, not configured), or a DNS problem */
  reason?: string
  release_no?: number
  image_tag?: string
  /** node names are <slug>.<zone>; empty = the form asks for hostname */
  dns_zone?: string
  /** the release's components (HOLD-A8); absent: a schema 1 release */
  components?: Partial<Record<ComponentName, ReleaseComponent>>
}

/** GET /api/admin/provisioning/keys; 404 = provisioning not configured. */
export interface ProvisioningKeys {
  ssh_authorized_key: string
  ssh_fingerprint: string
  root_fingerprint?: string
  install?: InstallInfo
  /** the center's routing network switch (RELAY_ENABLED): the default of the install form's relay option (PROV-RELAY-1) */
  relay_enabled?: boolean
  ops_serial?: number
  ops_fingerprint?: string
  ops_not_after?: string
}

export interface HostCreateRequest {
  address: string
  ssh_port?: number
  ssh_user: string
  ssh_host_key_sha256?: string
  slug: string
  /** only without a DNS zone (install.dns_zone empty) */
  hostname?: string
  region: string
  role?: NodeRole
  egress?: NodeEgress
  keep_ssh_access?: boolean
  totp?: string
  /** reboot once the node joined and is healthy (default none) */
  reboot?: RebootPlan
  /** join the routing network: install.sh --relay, relay_enabled in the join token; absent = the center's switch (PROV-RELAY-1) */
  relay?: boolean
}

export interface HostCommandRequest {
  kind: HostCommandKind
  args?: Record<string, unknown>
  totp?: string
  confirm?: string
  /** deploy and redeploy: reboot once the node came up healthy */
  reboot?: RebootPlan
  /** reboot although streams or outputs run on the node */
  confirm_live?: boolean
  /** one per confirmation: the same id again within 10 min gets the first answer, not a second command (PROV-RELAY-1) */
  request_id?: string
}

export type HostErrorCode =
  | 'totp_not_enrolled'
  | 'totp_required'
  | 'totp_invalid'
  | 'totp_locked'
  | 'confirm_required'
  | 'live_on_node'
  | 'offline'
  | 'bad_request'
  | 'not_found'
  | 'no_release'
  | 'busy'
  | 'not_failed'
  | 'not_configured'
  | 'provisioner_unavailable'
  /** relay_enable / relay_disable: the host has no live media node in the center */
  | 'no_node'
  | 'node_revoked'
  /** HOLD-A8: restart_node / update on a monolith node (nothing changed) */
  | 'node_not_split'
  /** update: the agent lacks the caps (deploy the release first) */
  | 'needs_deploy'
  /** update: the center's release names no components */
  | 'no_components'
  /** update: the center does not know the node's versions yet */
  | 'changes_unknown'
  /** update: the node already runs this component */
  | 'up_to_date'
  /** a split node with publishers and a release that replaces the holders: free the node first */
  | 'publishers_present'

export interface HostError {
  error: string
  code?: HostErrorCode
  retry_after_s?: number
  /** with confirm_required: the word to type */
  confirm?: string
  /** with live_on_node: what runs on the node now */
  live?: LiveCount
  /** with publishers_present: publishers on the node and what the release changes there */
  publishers?: number
  changes?: string[]
  /** network actions (ConfirmError, R0.9g): what the action takes from live routes */
  impact?: NodeImpact
}

export interface TOTPStatus {
  enrolled: boolean
  /** the last code still covers commands until then */
  confirmed_until?: string
}

/** The HostError of a failed request, or undefined if it was not one. */
export function hostError(e: unknown): (HostError & { status: number }) | undefined {
  if (!(e instanceof ApiError)) return undefined
  const d = (e.data ?? {}) as Partial<HostError>
  return { error: d.error ?? e.message, code: d.code, retry_after_s: d.retry_after_s, confirm: d.confirm, live: d.live, impact: d.impact, status: e.status }
}

/** SSE of hosts and installs: host.host, host.command, host.job. */
export const HOSTS_EVENTS_PATH = '/api/admin/hosts/events'

export interface HostEventHost {
  host_id: string
  online: boolean
}
export interface HostEventCommand {
  host_id: string
  command: HostCommand
}
export interface HostEventJob {
  host_id: string
  job: InstallJob
}
export interface HostEventReboot {
  host_id: string
  reboot: RebootWatch
}

const enc = encodeURIComponent

export const hostsApi = {
  keys: () => get<ProvisioningKeys>('/api/admin/provisioning/keys'),
  list: () => get<Host[]>('/api/admin/hosts'),
  host: (id: string) => get<Host>(`/api/admin/hosts/${enc(id)}`),
  jobs: (id: string) => get<InstallJob[]>(`/api/admin/hosts/${enc(id)}/jobs`),
  create: (body: HostCreateRequest) => post<InstallJob>('/api/admin/hosts', body),
  job: (jobId: string) => get<InstallJob>(`/api/admin/nodes/provisioning/${enc(jobId)}`),
  retry: (jobId: string, totp?: string) => post<InstallJob>(`/api/admin/nodes/provisioning/${enc(jobId)}/retry`, totp ? { totp } : {}),
  command: (id: string, body: HostCommandRequest) => post<HostCommand>(`/api/admin/hosts/${enc(id)}/commands`, body),
  totp: () => get<TOTPStatus>('/api/admin/me/totp'),
}

/* Signing in an app from the browser (docs/bridge-auth.md): the consent screen, /device, Devices & apps. */

/** What an app asks for (GET /api/oauth2/authorize, GET /api/oauth2/device). */
export interface Consent {
  client_id: string
  client_name: string
  scopes: string[]
  device_name?: string
}

/** A signed-in app (GET /api/me/devices). */
export interface Device {
  id: number
  kind: 'device'
  client_id: string
  name: string
  scopes: string[]
  app_version?: string
  created_at: string
  last_used_at?: string
  last_ip?: string
  expires_at?: string
}

/** The parameters of /oauth/authorize the center checks (AuthorizeRequest); the rest are dropped. */
export const AUTHORIZE_PARAMS = ['response_type', 'client_id', 'redirect_uri', 'code_challenge', 'code_challenge_method', 'state', 'scope', 'device'] as const

export const appAuthApi = {
  consent: (search: URLSearchParams) => get<Consent>(`/api/oauth2/authorize?${search}`),
  decide: (params: Record<string, string>, decision: 'allow' | 'deny') => post<{ redirect: string }>('/api/oauth2/authorize', { ...params, decision }),
  deviceConsent: (userCode: string) => get<Consent>(`/api/oauth2/device?user_code=${encodeURIComponent(userCode)}`),
  deviceDecide: (userCode: string, decision: 'allow' | 'deny') => post<void>('/api/oauth2/device/decide', { user_code: userCode, decision }),
  devices: () => get<{ devices: Device[] }>('/api/me/devices'),
  revoke: (id: number) => del<void>(`/api/me/devices/${id}`),
  revokeAll: () => post<{ revoked: number }>('/api/me/devices/revoke-all'),
}

// --- API tokens (docs/api-tokens.md, API-TOKENS-2) ---

/** An API token (sxk_) or an admin token (sxak_) as listed: never the secret. */
export interface APIToken {
  id: number
  kind: 'api_token' | 'admin_token'
  name: string
  scopes: string[]
  /** the prefix and the last 4 characters: sxk_…2aQx */
  hint: string
  created_at: string
  /** null = no expiry */
  expires_at: string | null
  last_used_at: string | null
  last_ip?: string
  created_ip?: string
  /** the owner (console lists) */
  user_id?: number
  user_email?: string
}

export interface APITokenList {
  tokens: APIToken[]
  limit: number
  /** the scopes the caller may put into a new token */
  scopes: string[]
  /** the choices; 0 = no expiry */
  expiry_days: number[]
  default_days: number
  /** false: the plan has no API tokens (the service; the box always has them) */
  available: boolean
  reason?: 'plan_feature_required'
}

export interface APITokenCreate {
  name: string
  scopes: string[]
  expires_in_days?: number
  /** admin tokens: the admin's code */
  totp?: string
}

/** The answer of a create: the secret is here only, shown once. */
export interface APITokenCreated {
  token: string
  api_token: APIToken
}

export const apiTokensApi = {
  list: () => get<APITokenList>('/api/me/api-tokens'),
  create: (b: APITokenCreate) => post<APITokenCreated>('/api/me/api-tokens', b),
  revoke: (id: number) => del<void>(`/api/me/api-tokens/${id}`),
  adminList: () => get<APITokenList>('/api/admin/api-tokens'),
  adminCreate: (b: APITokenCreate) => post<APITokenCreated>('/api/admin/api-tokens', b),
  adminRevoke: (id: number) => del<void>(`/api/admin/api-tokens/${id}`),
  userTokens: () => get<{ tokens: APIToken[] }>('/api/admin/user-tokens'),
  revokeUserToken: (id: number) => del<void>(`/api/admin/user-tokens/${id}`),
}

// --- Status page (docs/status-page.md, OPS-1) ---

export type StatusKind = 'incident' | 'maintenance'
export type StatusImpact = 'minor' | 'major' | 'critical' | 'maintenance'
export type StatusState = 'investigating' | 'identified' | 'monitoring' | 'resolved' | 'scheduled' | 'in_progress' | 'completed'

export interface StatusUpdate {
  status: StatusState
  message: string
  at: string
}

export interface StatusIncident {
  id: string
  kind: StatusKind
  title: string
  impact: StatusImpact
  status: StatusState
  components: string[]
  created_at: string
  updated_at: string
  resolved_at: string | null
  starts_at: string | null
  ends_at: string | null
  updates: StatusUpdate[]
}

export interface AdminStatus {
  configured: boolean
  page_url?: string
  components?: { id: string; name: string }[]
  incidents?: StatusIncident[]
  subscribers?: number
  email?: boolean
}

export interface StatusIncidentInput {
  kind: StatusKind
  title: string
  message: string
  components: string[]
  impact?: Exclude<StatusImpact, 'maintenance'>
  status?: StatusState
  starts_at?: string
  ends_at?: string
}

/** One incident or maintenance of the status page that touches the user (GET /api/status). */
export interface StatusBannerItem {
  id: string
  kind: StatusKind
  title: string
  status: StatusState
  impact: StatusImpact
  components: string[]
  updated_at: string
  starts_at?: string
  ends_at?: string
}

export const statusApi = {
  admin: () => get<AdminStatus>('/api/admin/status'),
  create: (body: StatusIncidentInput, totp?: string) => post<StatusIncident>('/api/admin/status/incidents', totp ? { ...body, totp } : body),
  update: (id: string, body: { status: StatusState; message: string }, totp?: string) =>
    post<StatusIncident>(`/api/admin/status/incidents/${encodeURIComponent(id)}/updates`, totp ? { ...body, totp } : body),
  mine: () => get<{ page_url: string; items: StatusBannerItem[] }>('/api/status'),
  /** No session needed: the sign-in pages' footer. */
  pageLink: () => get<{ page_url: string }>('/api/status/page'),
}
